<script setup lang="ts">
import type { OpenModalPayload } from '.';
import type { Property, PropertyImage } from '~/core/entities/property';

import { getPropertyImageAlt } from '~/utils';
import { grammarInflection } from '~~/utils';

import TextLink from '~/components/ui/text-link/TextLink.vue';

import messages from './PropertyHighlightedImagesSection.locale.json';

const props = defineProps<{
	images: Property['images'];
	isLoading?: boolean;
	propertyType?: string | null;
	suites?: number | null;
	region?: string | null;
	city?: string | null;
	askingPrice?: number | null;
}>();

const emit = defineEmits<{
	(e: 'open-modal', payload: OpenModalPayload): void;
}>();

const openModal = (params: {
	clickType: 'photo' | 'cta';
	image?: PropertyImage;
	ctaText?: string;
}) => {
	const photoUrl = params.image?.watermarkUrl || params.image?.url;

	emit('open-modal', {
		clickType: params.clickType,
		photoUrl,
		ctaText: params.ctaText
	});
};

const hasNecessaryImages = computed(() => props.images.length >= 9);
const firstSlice = computed(() => props.images.slice(4, 6));
const secondSlice = computed(() => props.images.slice(6, 9));

const { t } = useI18n({
	useScope: 'local',
	messages
});

const ctaText = computed(() =>
	t('cta', {
		count: props.images.length,
		item: grammarInflection(props.images.length, 'foto', 'fotos')
	})
);

const getImageAlt = (index: number | null) => {
	return getPropertyImageAlt(
		props.propertyType,
		props.suites,
		props.region,
		props.city,
		props.askingPrice,
		index
	);
};
</script>

<template>
	<div v-if="isLoading">
		<div class="grid grid-cols-6 gap-3 pb-5">
			<Skeleton
				:is-active="true"
				class="col-span-3 h-[310px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-3 h-[310px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-2 h-[220px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-2 h-[220px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-2 h-[220px]"
				variant="white"
			/>
		</div>

		<Skeleton :is-active="true" class="h-5 w-[120px]" variant="white" />
	</div>

	<div
		v-else-if="hasNecessaryImages"
		class="relative flex w-full flex-col gap-3 overflow-hidden"
	>
		<div
			class="relative grid size-full max-h-[306px] min-h-[240px] grid-cols-1 grid-rows-1 gap-3 overflow-hidden sm:grid-rows-1 md:grid-cols-2"
		>
			<NuxtImg
				v-for="(image, index) in firstSlice"
				:key="image?.url || index"
				:src="image?.watermarkUrl || image?.url"
				width="800"
				sizes="xs:100vw sm:100vw md:50vw lg:50vw xl:50vw 2xl:50vw"
				densities="x1"
				format="webp"
				:alt="getImageAlt(index + 3)"
				class="size-full cursor-pointer object-cover"
				:class="{ 'hidden md:block': index === firstSlice.length - 1 }"
				decoding="async"
				loading="lazy"
				@click="
					openModal({
						clickType: 'photo',
						image
					})
				"
			/>
		</div>
		<div
			class="relative grid max-h-[216px] grid-cols-2 grid-rows-1 gap-3 overflow-hidden md:grid-cols-3 md:grid-rows-[repeat(1,216px)]"
		>
			<NuxtImg
				v-for="(image, index) in secondSlice"
				:key="image?.url"
				:src="image?.watermarkUrl || image?.url"
				width="640"
				sizes="xs:50vw sm:50vw md:33vw lg:33vw xl:33vw 2xl:33vw"
				densities="x1"
				format="webp"
				:alt="getImageAlt(index + 5)"
				class="size-full cursor-pointer object-cover"
				:class="{ 'hidden md:block': index === secondSlice.length - 1 }"
				decoding="async"
				loading="lazy"
				@click="
					openModal({
						clickType: 'photo',
						image
					})
				"
			/>
		</div>
		<button
			class="flex items-center gap-2"
			@click="openModal({ clickType: 'cta', ctaText })"
		>
			<IconMaterialSymbolsLightImageOutline class="size-5" />
			<TextLink>
				{{ ctaText }}
			</TextLink>
		</button>
	</div>
</template>
